import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { FC, useEffect, useState } from 'react'
import { useDebounce, useWindowSize } from 'usehooks-ts'

import EmptyState from '@/components/EmptyState'
import { appSetIsLoading } from '@/reducers/app'
import { useLazyGetGalleryAlbumListQuery } from '@/services/gallery'
import { useAppDispatch } from '@/utils/hooks'

import AlbumCard from './components/AlbumCard'
import Pagination from '../components/Pagination'

import NotFoundStateImage from '~/assets/images/media/album-not-found-state.webp'

interface AlbumContent {
  locale?: string
  filterArea: string | null
  filterFind: string
  filterYear: string | null
  handleResetFilter: () => void
}

const AlbumContent: FC<AlbumContent> = ({ locale, filterArea, filterFind, filterYear, handleResetFilter }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { width } = useWindowSize()

  const [limit, _setLimit] = useState(12)
  const [skip, setSkip] = useState(0)
  const [page, setPage] = useState(0)
  const filterFindDebounce = useDebounce(filterFind, width <= 768 ? 1000 : 500)
  const [getAlbumList, { data, isFetching, isSuccess }] = useLazyGetGalleryAlbumListQuery()
  const [getInitialAlbumList, { data: initialData }] = useLazyGetGalleryAlbumListQuery()

  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * limit) % Number(data?.data.metadata.total)
    setPage(event.selected)
    setSkip(newOffset)
  }

  useEffect(() => {
    dispatch(appSetIsLoading(true))
    getInitialAlbumList({
      params: {
        skip: skip,
        limit: limit,
        sortBy: 'latest',
        keyword: filterFindDebounce || undefined,
        sezRegionProfileXid: filterArea || undefined,
        year: filterYear || undefined,
      },
      headers: { 'Accept-Language': String(router.locale) },
    })
      .unwrap()
      .then(() => {
        dispatch(appSetIsLoading(false))
      })
  }, [])

  useEffect(() => {
    dispatch(appSetIsLoading(true))
    getAlbumList({
      params: {
        skip: skip,
        limit: limit,
        sortBy: 'latest',
        keyword: filterFindDebounce || undefined,
        sezRegionProfileXid: filterArea || undefined,
        year: filterYear || undefined,
      },
      headers: { 'Accept-Language': String(router.locale) },
    })
      .unwrap()
      .then(() => {
        dispatch(appSetIsLoading(false))
      })
  }, [skip, limit, filterArea, filterFindDebounce, filterYear])

  return (
    <section className='container flex w-full flex-col gap-6 pb-12.5 pt-6 lg:gap-12.5 lg:py-12.5'>
      {initialData && initialData.data.items.length == 0 ? (
        <EmptyState
          img={NotFoundStateImage.src}
          title={t('album:emptyState.title')}
          description={t('album:emptyState.description')}
          imgClass='mx-auto w-[166px] h-[161px] lg:w-[333px] lg:h-[321px]'
          containerClass='self-center mt-[34px] mb-[50px] lg:mb-[100px]'
          buttonText={t('album:notFoundState.button') || undefined}
          onClick={handleResetFilter}
        />
      ) : (
        <>
          {!isFetching && isSuccess && data && data.data.items.length > 0 && (
            <>
              <div className='grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-[31.5px] lg:gap-y-8'>
                {data.data.items.map((card) => (
                  <AlbumCard
                    locale={locale}
                    data={card}
                    href='/media/album'
                    key={card.xid}
                  />
                ))}
              </div>
              <Pagination
                onPageChange={handlePageClick}
                selected={page}
                perPage={limit}
                totalData={data?.data.metadata.total ?? 0}
              />
            </>
          )}

          {!isFetching && isSuccess && data && data.data.items.length == 0 && (
            <EmptyState
              img={NotFoundStateImage.src}
              title={t('album:notFoundState.title')}
              description={t('album:notFoundState.description')}
              imgClass='mx-auto w-[166px] h-[161px] lg:w-[333px] lg:h-[321px]'
              containerClass='self-center mt-[34px] mb-[50px] lg:mb-[100px]'
              buttonText={t('album:notFoundState.button') || undefined}
              onClick={handleResetFilter}
            />
          )}
        </>
      )}
    </section>
  )
}

export default AlbumContent
